export type PrivacyPolicySubsection = {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
};

export type PrivacyPolicySection = {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
  subsections?: PrivacyPolicySubsection[];
};

export const PRIVACY_POLICY_CONTENT = {
  title: "VEVI Monster Privacy Policy",
  lastUpdated: "Last updated: June 5, 2026",
  seoTitle: "Privacy Policy | VEVI Monster",
  seoDescription:
    "Learn how VEVI Monster collects, uses, stores, and protects personal data when you use our AI dubbing, captions, audio translation, and localization tools.",
  backToHomeLabel: "Back to Home",
  controller: {
    heading: "1. Who We Are",
    intro:
      "This Privacy Policy explains how VEVI Monster collects, uses, stores, and shares personal data when you visit vevi.monster, upload media, request dubbing or captions, make a payment, or contact us.",
    companyName: "General Secret OÜ",
    companyDetails:
      "Registry code 17112803, Vesivärava tn 50-201, Kesklinna linnaosa, Tallinn, Harju maakond, 10152, Estonia",
    email: "site@vevi.monster",
  },
  sections: [
    {
      title: "2. What This Policy Covers",
      paragraphs: [
        "This policy applies to the VEVI Monster website, landing pages, upload flows, checkout flows, support channels, and related product experiences available through vevi.monster.",
        "It does not apply to third-party websites, payment pages, analytics services, or external platforms that operate under their own privacy policies.",
      ],
    },
    {
      title: "3. Personal Data We Collect",
      subsections: [
        {
          title: "Information you provide directly",
          bullets: [
            "Email address",
            "Uploaded video or audio files, file names, and related media metadata",
            "Source language, target language, subtitles preferences, and other processing settings",
            "Messages you send through site chat, email, or support requests",
          ],
        },
        {
          title: "Information collected automatically",
          bullets: [
            "IP address and approximate location",
            "Browser, device, and operating system information",
            "Pages viewed, session timing, navigation events, and referrer data",
            "Cookie and language-preference data needed for analytics or site functionality",
          ],
        },
        {
          title: "Payment-related information",
          paragraphs: [
            "Payments are processed by Revolut and related payment providers. We may receive limited payment details such as payment status, amount, currency, and transaction identifiers, but we do not store full card details on VEVI Monster.",
          ],
        },
      ],
    },
    {
      title: "4. How We Use Personal Data",
      bullets: [
        "To provide AI dubbing, captions, lip sync, audio translation, and related localization services",
        "To upload, process, store temporarily, and deliver media files and generated outputs",
        "To contact you about processing status, delivery, billing, support, or service issues",
        "To operate, secure, debug, and improve the website and backend infrastructure",
        "To detect abuse, fraud, or unauthorized use of the service",
        "To comply with legal, tax, accounting, and regulatory obligations",
      ],
    },
    {
      title: "5. Legal Bases for Processing",
      paragraphs: [
        "Where GDPR applies, we process personal data on the basis of contractual necessity when we provide the service you requested, legitimate interests when we secure and improve the service, legal obligations when we must retain or disclose information, and consent where consent is required for a specific activity.",
      ],
    },
    {
      title: "6. Third-Party Services and Processors",
      subsections: [
        {
          title: "Google Analytics 4",
          paragraphs: [
            "We use Google Analytics 4 to understand traffic, page usage, device patterns, and high-level audience behavior. Google may process browser identifiers, approximate location, device data, and usage events on our behalf.",
          ],
        },
        {
          title: "Revolut and payment providers",
          paragraphs: [
            "We use Revolut checkout and related payment infrastructure to process paid orders. These providers process payment and anti-fraud information under their own compliance obligations.",
          ],
        },
        {
          title: "Cloud hosting and media processing infrastructure",
          paragraphs: [
            "We rely on hosting, storage, and backend infrastructure providers to receive uploads, process files, store temporary outputs, and deliver completed results.",
          ],
        },
        {
          title: "Customer support chat",
          paragraphs: [
            "Our website includes a support chat widget and related communication infrastructure. If you use chat, messages and technical session data may be processed to provide support.",
          ],
        },
      ],
    },
    {
      title: "7. Cookies and Similar Technologies",
      paragraphs: [
        "We use cookies and similar technologies for essential site functionality, analytics, language preferences, and support tooling. Some cookies are set by third-party services such as Google Analytics or our support chat.",
        "For more detail about cookie categories and browser controls, please review our Cookie Policy.",
      ],
    },
    {
      title: "8. How We Share Personal Data",
      paragraphs: [
        "We do not sell your personal data.",
        "We may share personal data only when needed to operate the service and meet legal obligations.",
      ],
      bullets: [
        "With service providers and processors acting on our behalf",
        "With payment providers for checkout, billing, and fraud prevention",
        "When required by law, court order, or regulatory request",
        "As part of a merger, acquisition, financing, or business transfer",
      ],
    },
    {
      title: "9. Data Retention",
      paragraphs: [
        "We keep personal data only for as long as necessary to provide the service, deliver outputs, resolve support issues, maintain security, and comply with legal or accounting obligations.",
        "Uploaded media and generated files may be stored temporarily for processing and delivery. Analytics and technical logs may be kept for shorter operational periods, while payment and accounting records may be retained longer where required by law.",
      ],
    },
    {
      title: "10. International Data Transfers",
      paragraphs: [
        "Some of our providers may process personal data outside your country of residence. When this happens, we rely on appropriate contractual, organizational, or legal safeguards required under applicable data protection law.",
      ],
    },
    {
      title: "11. Security",
      paragraphs: [
        "We use reasonable technical and organizational measures to protect personal data, uploads, and payment-related workflows. However, no website, storage system, or internet transmission is completely secure.",
      ],
    },
    {
      title: "12. Your Rights",
      paragraphs: [
        "Depending on your location and applicable law, you may have the right to access, correct, delete, or port your personal data, object to or restrict certain processing, withdraw consent, and file a complaint with a supervisory authority.",
        "To exercise a privacy request, email us at site@vevi.monster.",
      ],
    },
    {
      title: "13. Children's Privacy",
      paragraphs: [
        "VEVI Monster is not intended for children under 13, and we do not knowingly collect personal data from children. If you believe a child provided personal data to us, please contact us so we can review and delete it where appropriate.",
      ],
    },
    {
      title: "14. Changes to This Privacy Policy",
      paragraphs: [
        "We may update this Privacy Policy from time to time to reflect product, legal, or operational changes. When we do, we will update the revision date on this page.",
      ],
    },
    {
      title: "15. Contact",
      paragraphs: [
        "If you have questions about this Privacy Policy or our data practices, contact us at site@vevi.monster.",
      ],
    },
  ] satisfies PrivacyPolicySection[],
} as const;
